import { useEffect, useId, useRef, useState } from 'react';
import { CATEGORY_LABELS, subtypesForCategory } from '@/lib/catalog';
import { useCatalog } from '@/hooks/CatalogContext';
import type { DrinkCategory, SortKey } from '@/types/data';

const categories: Array<DrinkCategory | 'all'> = [
  'all',
  'beer',
  'liquor',
  'wine',
  'mixed-drinks',
  'seltzers',
  'cider',
];

const OPTION_FILTERS = [
  {
    key: 'verifiedNutritionOnly',
    label: 'Verified nutrition only',
  },
  {
    key: 'pricedOnly',
    label: 'Priced items only',
  },
  {
    key: 'includeZeroAbv',
    label: 'Include 0% ABV (non-alcoholic)',
  },
] as const;

function optionFilterSummary(selected: number): string {
  if (selected === 0) return 'None selected';
  if (selected === 1) return '1 selected';
  return `${selected} selected`;
}

export function FilterBar({
  lockedCategory,
}: {
  lockedCategory?: DrinkCategory;
}) {
  const {
    filters,
    setFilters,
    clearFilters,
    prefs,
    setSort,
    setVolumeUnit,
    rows,
  } = useCatalog();
  const [optionsOpen, setOptionsOpen] = useState(false);
  const optionsRef = useRef<HTMLDivElement>(null);
  const optionsMenuId = useId();

  const category = lockedCategory ?? filters.category;
  const subtypes = subtypesForCategory(rows, category);
  const selectedOptionCount = OPTION_FILTERS.filter((opt) => filters[opt.key]).length;

  useEffect(() => {
    if (!optionsOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!optionsRef.current?.contains(event.target as Node)) {
        setOptionsOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOptionsOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [optionsOpen]);

  return (
    <div className="toolbar">
      <label className="sr-only" htmlFor="search">
        Search drinks
      </label>
      <input
        id="search"
        className="search-input"
        type="search"
        placeholder="Search name, brand, subtype…"
        value={filters.query}
        onChange={(e) => setFilters({ query: e.target.value })}
      />

      <div className="chip-row" role="group" aria-label="Category">
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            className="chip"
            aria-pressed={category === cat}
            disabled={Boolean(lockedCategory) && cat !== lockedCategory}
            onClick={() =>
              setFilters({
                category: cat,
                subtype: null,
              })
            }
          >
            {CATEGORY_LABELS[cat]}
          </button>
        ))}
      </div>

      <div className="filters-panel">
        <label>
          Subtype
          <select
            className="select"
            value={filters.subtype ?? ''}
            onChange={(e) =>
              setFilters({ subtype: e.target.value || null })
            }
          >
            <option value="">All subtypes</option>
            {subtypes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label>
          Sort
          <select
            className="select"
            value={`${prefs.sortKey}:${prefs.sortDirection}`}
            onChange={(e) => {
              const [key, dir] = e.target.value.split(':') as [SortKey, 'asc' | 'desc'];
              setSort(key, dir);
            }}
          >
            <option value="alcoholPer100Calories:desc">
              Alcohol ({prefs.volumeUnit === 'fl_oz' ? 'fl oz' : 'mL'}) / 100 cal ↓
            </option>
            <option value="alcoholPer100Calories:asc">
              Alcohol ({prefs.volumeUnit === 'fl_oz' ? 'fl oz' : 'mL'}) / 100 cal ↑
            </option>
            <option value="alcoholPerDollar:desc">
              Alcohol ({prefs.volumeUnit === 'fl_oz' ? 'fl oz' : 'mL'}) / $ ↓
            </option>
            <option value="alcoholPerDollar:asc">
              Alcohol ({prefs.volumeUnit === 'fl_oz' ? 'fl oz' : 'mL'}) / $ ↑
            </option>
            <option value="name:asc">Name A–Z</option>
            <option value="abv:desc">ABV ↓</option>
            <option value="calories:asc">Calories ↑</option>
          </select>
        </label>
        <label>
          Volume units
          <select
            className="select"
            value={prefs.volumeUnit}
            onChange={(e) => setVolumeUnit(e.target.value as 'ml' | 'fl_oz')}
          >
            <option value="ml">Milliliters (mL)</option>
            <option value="fl_oz">US fluid ounces</option>
          </select>
        </label>
        <label>
          Max calories / serving
          <input
            className="input"
            type="number"
            min={0}
            placeholder="Any"
            value={filters.caloriesMax ?? ''}
            onChange={(e) =>
              setFilters({
                caloriesMax: e.target.value ? Number(e.target.value) : null,
              })
            }
          />
        </label>
        <label>
          Max carbs / serving (g)
          <input
            className="input"
            type="number"
            min={0}
            placeholder="Any"
            value={filters.carbsMax ?? ''}
            onChange={(e) =>
              setFilters({
                carbsMax: e.target.value ? Number(e.target.value) : null,
              })
            }
          />
        </label>
        <label>
          ABV min
          <input
            className="input"
            type="number"
            min={0}
            max={100}
            step={0.1}
            value={filters.abvMin ?? ''}
            onChange={(e) =>
              setFilters({
                abvMin: e.target.value ? Number(e.target.value) : null,
              })
            }
          />
        </label>
        <label>
          ABV max
          <input
            className="input"
            type="number"
            min={0}
            max={100}
            step={0.1}
            value={filters.abvMax ?? ''}
            onChange={(e) =>
              setFilters({
                abvMax: e.target.value ? Number(e.target.value) : null,
              })
            }
          />
        </label>
        <div className="multi-select" ref={optionsRef}>
          <span className="multi-select__label" id={`${optionsMenuId}-label`}>
            Other Options
          </span>
          <button
            type="button"
            className="select multi-select__trigger"
            aria-haspopup="listbox"
            aria-expanded={optionsOpen}
            aria-controls={optionsMenuId}
            aria-labelledby={`${optionsMenuId}-label`}
            onClick={() => setOptionsOpen((open) => !open)}
          >
            {optionFilterSummary(selectedOptionCount)}
          </button>
          {optionsOpen && (
            <div
              id={optionsMenuId}
              className="multi-select__menu"
              role="listbox"
              aria-multiselectable="true"
              aria-labelledby={`${optionsMenuId}-label`}
            >
              {OPTION_FILTERS.map((opt) => {
                const checked = filters[opt.key];
                return (
                  <label key={opt.key} className="multi-select__option">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) =>
                        setFilters({ [opt.key]: e.target.checked })
                      }
                    />
                    <span>{opt.label}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <div className="clear-filters">
          <span className="clear-filters__label">Reset</span>
          <button
            type="button"
            className="btn clear-filters__button"
            onClick={() => clearFilters(lockedCategory)}
          >
            Clear filters
          </button>
        </div>
      </div>
      <p className="muted" style={{ margin: 0, fontSize: '0.85rem' }}>
        Per-serving filters (calories/carbs) are separate from ratio rankings.
        Missing values sort last.
      </p>
    </div>
  );
}
